import {
  createContext,
  useContext,
  useState,
  useEffect,
  useMemo,
  useCallback,
} from "react";
import { useNavigate } from "react-router-dom";
import { useLocalStorage } from "../hooks/useLocalStorage";
import { useQueryParam } from "../hooks/useQueryParam";
import { useRefreshToken, useAuthLogout } from "../services/authService";
import { useThemeManager } from "../hooks/useThemeManager";

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const navigate = useNavigate();
  const [authData, setAuthData] = useLocalStorage("session", null);
  const [accountData, setAccount] = useLocalStorage("account", null);
  const redirect = useQueryParam("redirect");
  const isProPlan = useQueryParam("plan") === "pro";
  const themeManager = useThemeManager();
  const [hasInitializedTheme, setHasInitializedTheme] = useState(false);

  const [isInitialized, setIsInitialized] = useState(false);

  const { mutateAsync: logoutAPI } = useAuthLogout();
  const { mutateAsync: refreshToken } = useRefreshToken();

  // Check token validity on mount and set up periodic check
  useEffect(() => {
    const validateToken = () => {
      if (!authData) {
        setIsInitialized(true);
        return;
      }

      if (isTokenExpired(authData)) {
        // Token is expired, log the user out
        handleLogout(false); // Silent logout (no API call)
      } else {
        setIsInitialized(true);
      }
    };
    // Initial validation
    validateToken();

    // Set up periodic validation (every 5 minutes)
    const interval = setInterval(validateToken, 5 * 60 * 1000);
    return () => {
      clearInterval(interval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authData]);

  /**
   * Check if the token is expired
   * @param {string} token - The JWT token to check
   * @returns {boolean} - True if the token is expired, false otherwise
   * @throws Will throw an error if the token is invalid
   */
  const isTokenExpired = useCallback((token) => {
    if (!token) return true;

    try {
      // Parse JWT payload
      const payload = JSON.parse(atob(token.split(".")[1]));

      // Check if expiration time exists
      if (!payload.exp) return true;

      // Convert expiration time to milliseconds and compare with current time
      const expirationDate = payload.exp * 1000;
      return Date.now() > expirationDate;
    } catch (error) {
      console.error("Error checking token expiration:", error);
      return true;
    }
  }, []);

  const _cleanupLogout = useCallback(async () => {
    setAuthData(null);
    setAccount(null);

    localStorage.removeItem("session");
    localStorage.removeItem("account");

    // Short delay to ensure clearance completes
    await new Promise((resolve) => setTimeout(resolve, 50));
  }, [setAuthData, setAccount]);

  /**
   * Login function to set authentication data and account information
   * @param {string} token - The authentication token
   * @param {Object} account - The account information
   * @returns {Promise<void>}
   * @throws Will throw an error if the token is invalid
   */
  const login = useCallback(
    async (token, account) => {
      // First clear any existing data to prevent persistence issues
      await _cleanupLogout();

      // Then set the new data
      setAuthData(token);
      setAccount({
        ...account,
        lastFetch: Date.now(),
      });

      // Redirect to the specified page or default to home
      if (redirect) {
        navigate(redirect);
      } else if (isProPlan) {
        navigate("/settings/subscription?plan=pro");
      } else {
        navigate("/");
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [_cleanupLogout, setAuthData, setAccount, navigate]
  );

  const refresh = useCallback(
    async () => {
      try {
        const tokenData = await refreshToken();
        if (tokenData.status === "error") {
          throw new Error("Failed to refresh token");
        }
        const { tokens, account } = tokenData;

        const token = tokens.access.token;
        // Set new session data
        setAuthData(token);
        setAccount({
          ...account,
          lastFetch: Date.now(),
        });

        return { success: true, token, account };
      } catch (error) {
        // Don't logout on refresh errors - let the caller handle it
        return { success: false, error };
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [refreshToken, setAuthData, setAccount]
  );

  /**
   * Logout function to clear authentication data and redirect to login page
   * @param {boolean} callAPI - Whether to call the logout API
   * @returns {Promise<void>}
   * @throws Will throw an error if the API call fails
   */
  const handleLogout = useCallback(
    async (callAPI = true) => {
      try {
        // Call logout API if requested
        if (callAPI && authData) {
          await logoutAPI().catch((error) => {
            console.error("Error logging out from API:", error);
          });
        }
      } finally {
        // Clear local state regardless of API success
        await _cleanupLogout();

        // Redirect to login page
        window.location.href = "/signin";
      }
    },
    [authData, logoutAPI, _cleanupLogout]
  );
  // Handle errors - logout on authentication erro

  // Memoize expensive operations
  const isAuthenticated = useMemo(() => {
    return !!authData && !isTokenExpired(authData);
  }, [authData, isTokenExpired]);

  // Sync theme from account preferences only on initial login/app load
  useEffect(() => {
    if (
      accountData?.preferences?.theme &&
      isAuthenticated &&
      !hasInitializedTheme
    ) {
      // Only sync theme from account on first load or login
      const storedTheme = localStorage.getItem("theme");
      if (!storedTheme || storedTheme !== accountData.preferences.theme) {
        themeManager.syncThemeFromAccount(accountData.preferences.theme);
      }
      setHasInitializedTheme(true);
    }
  }, [
    accountData?.preferences?.theme,
    isAuthenticated,
    hasInitializedTheme,
    themeManager,
  ]);

  const logout = useCallback(() => handleLogout(true), [handleLogout]);

  // Create memoized context value
  const contextValue = useMemo(
    () => ({
      session: authData,
      account: accountData,
      isAuthenticated,
      isInitialized,
      login,
      logout,
      refresh,
      ...themeManager,
    }),
    [
      authData,
      accountData,
      isAuthenticated,
      isInitialized,
      login,
      logout,
      refresh,
      themeManager,
    ]
  );

  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
